import { useQuery } from '@tanstack/react-query';
import { getRouteApi } from '@tanstack/react-router';

import { LoadingPage } from '@nesso/ui/blocks/loading-page';

import { isExpiredPaid, subscriptionMeOptions, useCheckoutReturn } from '@/entities/subscription';
import { AddClient } from '@/features/client/add-client';
import { AddClientContact } from '@/features/client/add-client-contact';
import { EditClientProfile } from '@/features/client/edit-client-profile';
import { EditContactProfile } from '@/features/client/edit-contact-profile';
import { QuickAddClient } from '@/features/client/quick-add-client';
import { SearchClientNavigation } from '@/features/client/search-client';
import { SaveServiceSheetHost } from '@/features/practice/save-service';
import { ScheduleSessionDialog } from '@/features/session/schedule-session';
import { useSheetStore } from '@/shared/stores/sheet-store';
import { Page } from '@/shared/ui/layout/page';
import { NavigationBar } from '@/shared/ui/navigation/navigation-bar';
import { ClientDetailsSheet, openClientDetailsSheet } from '@/widgets/client/client-details-sheet';
import { ClientList } from '@/widgets/client/client-list';
import { ContactDetailsSheet } from '@/widgets/client/contact-details-sheet';
import { ClientBillingSheet } from '@/widgets/ledger/client-billing-sheet';
import { CreateMenu } from '@/widgets/navigation/create-menu';
import { ShareReferralLinkTrigger } from '@/widgets/referral/share-link-trigger';
import { AllPastSessions } from '@/widgets/session/all-past-sessions';
import { NewSession } from '@/widgets/session/new-session';
import { SessionDetails } from '@/widgets/session/session-details';
import { ResubscribeSheet } from '@/widgets/subscription/resubscribe-sheet';
import { TrialBanner } from '@/widgets/subscription/trial-banner';
import { UserDropdown } from '@/widgets/user/user-dropdown';

import { useEmailVerifiedToast } from '../model/use-email-verified-toast';
import { useOpenSheetFromSearch } from '../model/use-open-sheet-from-search';
import { DashboardExpiredTakeover } from './dashboard-page/dashboard-expired-takeover';
import { DashboardIllustration } from './dashboard-page/dashboard-illustration';
import { TodaySessions } from './dashboard-page/today-sessions';

const route = getRouteApi('/dashboard');

export function DashboardPage() {
    useOpenSheetFromSearch();
    useEmailVerifiedToast();

    const { checkout_session_id: checkoutSessionId } = route.useSearch();
    const { hasEhrAccess } = route.useRouteContext();
    const navigate = route.useNavigate();

    const { data: subscription } = useQuery(subscriptionMeOptions());

    const clearCheckoutParam = () =>
        navigate({
            search: (prev) => ({ ...prev, checkout_session_id: undefined }),
            replace: true,
        });

    const { isResubscribeSettling } = useCheckoutReturn(checkoutSessionId, clearCheckoutParam);

    const openSheet = useSheetStore((state) => state.openSheet);

    const isExpired = isExpiredPaid(subscription);

    if (isResubscribeSettling) {
        return <LoadingPage className="fixed inset-0 z-50" />;
    }

    return (
        <Page>
            <NavigationBar
                actions={
                    <>
                        {/* Gated with the workspace below it: the takeover removes the client list
                            and new-session surfaces, and search would otherwise still reach client
                            names and the add-client flow past it. */}
                        {!isExpired && (
                            <>
                                <SearchClientNavigation
                                    hasEhrAccess={hasEhrAccess}
                                    onSelectClient={openClientDetailsSheet}
                                />
                                <CreateMenu hasEhrAccess={hasEhrAccess} />
                            </>
                        )}
                        <ShareReferralLinkTrigger />
                    </>
                }
                dropdown={<UserDropdown />}
                banner={<TrialBanner />}
                mobileBanner={<TrialBanner variant="mobile" />}
                disableLogoNavigation
            />
            <TodaySessions>
                {isExpired ? (
                    <DashboardExpiredTakeover
                        deletionDate={subscription?.retentionEndsAt}
                        onChoosePlan={() =>
                            openSheet({ type: 'subscription/resubscribe', label: 'Choose plan' })
                        }
                    />
                ) : (
                    <>
                        <div className="relative z-1 flex flex-col items-center flex-1 min-h-0 py-4 px-2 sm:px-4 space-y-6 overflow-y-auto">
                            <NewSession hasEhrAccess={hasEhrAccess} />
                            <ClientList />
                        </div>
                        <DashboardIllustration />
                    </>
                )}
            </TodaySessions>
            <AllPastSessions />
            <SessionDetails />
            <QuickAddClient />
            <ScheduleSessionDialog />
            <SaveServiceSheetHost />
            <AddClient />
            <ClientDetailsSheet />
            <ContactDetailsSheet />
            <ClientBillingSheet />
            <EditClientProfile />
            <EditContactProfile />
            <AddClientContact />
            <ResubscribeSheet returnTo="/dashboard" />
        </Page>
    );
}
